import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { BankNameField } from '../lib/BankNameField.js';
import { api, put } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, Spinner } from '../lib/ui.js';
import { t } from '../i18n/index.js';

type BankDetails = {
  bank_name: string | null;
  bank_account_number: string | null;
  ifsc_code: string | null;
  pan_number: string | null;
};
const EMPTY: BankDetails = { bank_name: null, bank_account_number: null, ifsc_code: null, pan_number: null };

/**
 * Where this worker is paid, as the office sees it.
 *
 * The worker fills this in themselves during onboarding; this card exists for the ones who never
 * did, and for correcting a digit read wrong off a passbook. Either way the change is audited
 * with the old and new values, and a worker changing their own shows up in the admin feed.
 */
export function BankCard({ workerId }: { workerId: string }) {
  const [details, setDetails] = useState<BankDetails | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [account, setAccount] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [pan, setPan] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<BankDetails>(`/admin/workers/${workerId}/bank`);
      setDetails(r);
      setName(r.bank_name ?? '');
      setAccount(r.bank_account_number ?? '');
      setIfsc(r.ifsc_code ?? '');
      setPan(r.pan_number ?? '');
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
      setDetails(EMPTY);
    }
  }, [workerId]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await put(`/admin/workers/${workerId}/bank`, {
        bank_name: name.trim(),
        bank_account_number: account.trim(),
        ifsc_code: ifsc.trim(),
        pan_number: pan.trim(),
      });
      await load();
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="row between">
        <h3>{t('bank_title')}</h3>
        {!editing && details && (
          <button type="button" className="small secondary" onClick={() => setEditing(true)}>
            {details.bank_account_number ? t('worker_edit') : t('bank_save')}
          </button>
        )}
      </div>
      <p className="muted">{t('bank_admin_sub')}</p>
      {error && <Alert>{error}</Alert>}
      {saved && !editing && <Alert kind="success">{t('bank_admin_saved')}</Alert>}
      {details && !editing && details.bank_account_number && (!details.ifsc_code || !details.pan_number) && (
        <Alert kind="warn">{t('bank_incomplete')}</Alert>
      )}

      {!details ? (
        <Spinner />
      ) : editing ? (
        <form onSubmit={save} className="grid-form">
          <BankNameField value={name} onChange={setName} required />
          <label>
            {t('bank_account')}
            <input
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              required
              inputMode="numeric"
              maxLength={24}
              placeholder={t('bank_account_hint')}
            />
          </label>
          <label>
            {t('bank_ifsc')}
            <input
              value={ifsc}
              onChange={(e) => setIfsc(e.target.value.toUpperCase())}
              required
              maxLength={11}
              placeholder={t('bank_ifsc_hint')}
            />
          </label>
          <label>
            {t('bank_pan')}
            <input
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              required
              maxLength={10}
              placeholder={t('bank_pan_hint')}
            />
          </label>
          <div className="row">
            <button
              type="submit"
              disabled={busy || !name.trim() || !account.trim() || !ifsc.trim() || !pan.trim()}
            >
              {busy ? t('bank_saving') : t('bank_save')}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => {
                setName(details.bank_name ?? '');
                setAccount(details.bank_account_number ?? '');
                setIfsc(details.ifsc_code ?? '');
                setPan(details.pan_number ?? '');
                setEditing(false);
              }}
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      ) : details.bank_account_number ? (
        <table className="kv">
          <tbody>
            <tr>
              <th scope="row">{t('bank_name')}</th>
              <td>{details.bank_name}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_account')}</th>
              <td>{details.bank_account_number}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_ifsc')}</th>
              <td>{details.ifsc_code || <span className="muted">{t('bank_missing')}</span>}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_pan')}</th>
              <td>{details.pan_number || <span className="muted">{t('bank_missing')}</span>}</td>
            </tr>
          </tbody>
        </table>
      ) : (
        <Alert kind="warn">{t('bank_admin_none')}</Alert>
      )}
    </section>
  );
}
